// ── Main entry (Phase 2A + 2B + 2C) ──
//
// Two Bit-elements (per AR's definition):
//   - Wordmark dot (.brand-i-dot) is the GlassBitEcho — the brand mark on
//     Layer 1. Always at logo position. Never descends.
//   - Hero eyebrow square (.eyebrow-marker, data-bit-anchor=hero-eyebrow) is
//     the main character — the FieldBit. Descends into Layer 2 (field) on
//     scroll, returns to Layer 1 at Transfer.
//
// Both are DOM-resolved so the screen position tracks actual layout under
// font-load + responsive shifts. The eyebrow anchor is resolved per-frame
// because it scrolls with the page (its rect changes with scrollY).

import './styles.css';
import { setupLenis } from './scroll/lenis-setup.js';
import { FieldClockSystem } from './lib/clock.js';
import { FieldRenderer } from './canvas/field-renderer.js';
import { GlassRenderer, type LogoAnchor } from './canvas/glass-renderer.js';
import { viewerStageFor } from './canvas/viewer.js';
import { BootSequence } from './canvas/boot-sequence.js';
import { ILLUMINATION } from './lib/illumination-params.js';

const DEBUG = new URLSearchParams(location.search).has('debug');
const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const IS_TOUCH = window.matchMedia('(hover: none) and (pointer: coarse)').matches;

function glassOpacityFor(gp: number): number {
  if (gp <= 0.10) return 0.85;
  if (gp <= 0.40) return 0.85 - ((gp - 0.10) / 0.30) * 0.30;
  if (gp <= 0.65) return 0.55;
  if (gp <= 0.80) return 0.55 + ((gp - 0.65) / 0.15) * 0.10;
  return 0.65 + ((gp - 0.80) / 0.20) * 0.20;
}

function setGlassOpacity(opacity: number): void {
  document.documentElement.style.setProperty(
    '--glass-opacity-pct',
    `${(opacity * 100).toFixed(1)}%`,
  );
}

// CSS .eyebrow-marker is a no-JS fallback only. Once main.ts is alive, the
// canvas FieldBit IS the visible main character at the eyebrow position; the
// CSS marker is hidden permanently to avoid duplicate squares.
function hideStaticEyebrowMarker(): void {
  document.documentElement.style.setProperty('--eyebrow-marker-opacity', '0');
}

// ── Wordmark dot (GlassBitEcho) anchor ──
function resolveLogoAnchor(): LogoAnchor {
  const el =
    document.querySelector<HTMLElement>('[data-bit-anchor="brand-dot"]') ??
    document.querySelector<HTMLElement>('.brand-i-dot') ??
    document.querySelector<HTMLElement>('.brand-i');
  if (!el) {
    return { x: window.innerWidth / 2, y: 30, size: 12 };
  }
  const r = el.getBoundingClientRect();
  return {
    x: r.left + r.width / 2,
    y: r.top + r.height / 2,
    size: Math.max(8, Math.round(r.height)),
  };
}

// ── Hero eyebrow square (FieldBit origin) anchor ──
// Resolved per-frame because it scrolls with the page.
function resolveEyebrowAnchor(): LogoAnchor {
  const el = document.querySelector<HTMLElement>(
    '[data-bit-anchor="hero-eyebrow"]',
  );
  if (!el) {
    return { x: window.innerWidth / 2, y: 200, size: 10 };
  }
  const r = el.getBoundingClientRect();
  return {
    x: r.left + r.width / 2,
    y: r.top + r.height / 2,
    size: Math.max(8, Math.round(r.height)),
  };
}

// ── Phase 2C residue persistence ──
const RESIDUE_KEY = 'sb-residue-v1';
const RESIDUE_TTL_MS = 1000 * 60 * 60 * 24 * 30;

interface Residue {
  highestStoryTime: number;
  savedAt: number;
}

function loadResidue(): Residue | null {
  try {
    const raw = localStorage.getItem(RESIDUE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Residue>;
    if (typeof parsed.highestStoryTime !== 'number') return null;
    if (typeof parsed.savedAt !== 'number') return null;
    if (Date.now() - parsed.savedAt > RESIDUE_TTL_MS) return null;
    return { highestStoryTime: parsed.highestStoryTime, savedAt: parsed.savedAt };
  } catch {
    return null;
  }
}

function saveResidue(highestStoryTime: number): void {
  try {
    const payload: Residue = { highestStoryTime, savedAt: Date.now() };
    localStorage.setItem(RESIDUE_KEY, JSON.stringify(payload));
  } catch {
    /* fail silent */
  }
}

function init(): void {
  const fieldCanvas = document.getElementById('field-canvas') as HTMLCanvasElement | null;
  const glassCanvas = document.getElementById('glass-canvas') as HTMLCanvasElement | null;
  const bootCanvas = document.getElementById('boot-canvas') as HTMLCanvasElement | null;
  if (!fieldCanvas || !glassCanvas || !bootCanvas) {
    console.error('[SB] Required canvas elements not found');
    return;
  }

  const clockSystem = new FieldClockSystem();
  const fieldRenderer = new FieldRenderer(fieldCanvas);
  const glassRenderer = new GlassRenderer(glassCanvas);
  const bootSequence = new BootSequence(bootCanvas);

  const fieldBitSize = IS_TOUCH ? 22 : 32;
  fieldRenderer.setFieldBitSize(fieldBitSize);

  // Phase 2C: prime field with ghost residue from last visit.
  const residue = loadResidue();
  let residueApplied = false;
  if (residue) {
    fieldRenderer.applyGhostResidue(residue.highestStoryTime);
    residueApplied = true;
  }

  const updateLogoAnchor = (): void => {
    glassRenderer.setLogoAnchor(resolveLogoAnchor());
  };
  updateLogoAnchor();
  window.addEventListener('resize', updateLogoAnchor);
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(updateLogoAnchor).catch(() => {});
  }
  requestAnimationFrame(() => updateLogoAnchor());

  // Capture initial eyebrow position once (when scrollY ≈ 0). This becomes the
  // viewport-fixed "from" point for FieldBit's descent. Re-captures on resize
  // / font-load only when still at top of page, so a user-resize mid-scroll
  // doesn't snap FieldBit's origin to wherever the eyebrow has scrolled to.
  let initialEyebrow = resolveEyebrowAnchor();
  const recaptureEyebrow = (): void => {
    if (window.scrollY < 50) {
      initialEyebrow = resolveEyebrowAnchor();
    }
  };
  window.addEventListener('resize', recaptureEyebrow);
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(recaptureEyebrow).catch(() => {});
  }
  requestAnimationFrame(recaptureEyebrow);

  // Hide the static CSS eyebrow marker — canvas FieldBit owns this position now.
  hideStaticEyebrowMarker();

  if (REDUCED_MOTION) {
    fieldRenderer.setAlphaCap(ILLUMINATION.reducedMotionAlphaCap);
    setGlassOpacity(0.85);
    bootSequence.skip();
    document.body.classList.add('boot-complete');
    const clock = clockSystem.snapToDestination();
    fieldRenderer.draw(clock, 0, initialEyebrow);
    glassRenderer.draw();
    if (DEBUG) attachDebugHud(clockSystem, fieldRenderer, 'reduced-motion', residueApplied);
    return;
  }

  // ── Boot sequence (Phase 2D) ──
  // Skip the cinematic if the user has loaded mid-scroll (refresh while
  // scrolled, deep-link, restored session). The birth animation only reads
  // when the user is at the top of the page.
  const startsAtTop = window.scrollY < 50;
  let bootScheduled = false;
  if (startsAtTop) {
    // Boot-canvas Bit owns the protagonist during birth. Hide field-canvas Bit
    // until boot completes, then hand off at the same eyebrow position.
    fieldRenderer.setFieldBitVisible(false);
    document.body.classList.add('boot-active');
    // Defer one frame so font-load + layout settle and parent/target anchors
    // resolve correctly. Then start the timeline + the CSS dot keyframe.
    requestAnimationFrame(() => {
      const parentNow = resolveLogoAnchor();
      // Re-resolve initial eyebrow now that fonts/layout have settled.
      if (window.scrollY < 50) initialEyebrow = resolveEyebrowAnchor();
      bootSequence.start(parentNow, initialEyebrow, performance.now());
      bootScheduled = true;
    });
  } else {
    bootSequence.skip();
    document.body.classList.add('boot-complete');
  }

  const lenis = IS_TOUCH ? null : setupLenis();

  let visible = true;
  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver(
      (entries) => {
        const e = entries[0];
        if (e) visible = e.isIntersecting;
      },
      { threshold: 0 },
    );
    observer.observe(fieldCanvas);
  }

  if (IS_TOUCH) {
    fieldRenderer.setDprCap(1);
  }

  let lastTime = performance.now() / 1000;
  let bootCompleteFired = false;

  function frame(now: number): void {
    const t = now / 1000;
    const dt = Math.min(0.05, t - lastTime);
    lastTime = t;

    if (lenis) lenis.raf(now);

    // Boot sequence: render until done, then trigger veil + boot-canvas fade
    // by adding `body.boot-complete`. CSS handles the fade-out transition.
    if (bootScheduled && bootSequence.isRunning()) {
      bootSequence.draw(now);
    } else if (bootScheduled && bootSequence.isDone() && !bootCompleteFired) {
      // Hand off Bit from boot-canvas to field-canvas at the same position.
      fieldRenderer.setFieldBitVisible(true);
      document.body.classList.add('boot-complete');
      document.body.classList.remove('boot-active');
      bootCompleteFired = true;
    }

    if (visible && !document.hidden) {
      const scrollY = window.scrollY;
      const scrollMax = Math.max(
        1,
        document.documentElement.scrollHeight - window.innerHeight,
      );
      const clock = clockSystem.update(scrollY, scrollMax, dt);

      setGlassOpacity(glassOpacityFor(clock.glassProgress));

      fieldRenderer.draw(clock, dt, initialEyebrow);
      glassRenderer.draw();
    }

    requestAnimationFrame(frame);
  }

  requestAnimationFrame(frame);

  const persist = (): void => {
    saveResidue(clockSystem.get().highestStoryTime);
  };
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) persist();
  });
  window.addEventListener('beforeunload', persist);
  setInterval(persist, 15000);

  if (DEBUG) {
    attachDebugHud(
      clockSystem,
      fieldRenderer,
      IS_TOUCH ? 'touch (no Lenis)' : 'desktop (Lenis)',
      residueApplied,
    );
  }
}

function attachDebugHud(
  clockSystem: FieldClockSystem,
  fieldRenderer: FieldRenderer,
  mode: string,
  residueApplied: boolean,
): void {
  const hud = document.createElement('div');
  hud.style.cssText = [
    'position:fixed', 'top:10px', 'right:10px', 'z-index:9999',
    'font:11px ui-monospace,Menlo,monospace', 'color:#0f0',
    'background:rgba(0,0,0,0.85)', 'padding:8px 10px',
    'border:1px solid #0f0', 'pointer-events:none',
    'white-space:pre', 'line-height:1.5',
  ].join(';');
  document.body.appendChild(hud);

  const isStatic = mode === 'reduced-motion';

  function paint(): void {
    const c = clockSystem.get();
    const total = fieldRenderer.totalCells();
    const lit = fieldRenderer.litCount(0.15);
    const litPct = total > 0 ? ((lit / total) * 100).toFixed(1) : '0.0';
    const b2pass = c.glassProgress >= 0.08 && lit / total >= 0.05;
    const glassOp = glassOpacityFor(c.glassProgress);
    const viewerStage = viewerStageFor(c.glassProgress);

    let bitPhase = 'logo';
    if (c.glassProgress >= 0.05 && c.glassProgress < 0.40) bitPhase = 'descending';
    else if (c.glassProgress >= 0.40 && c.glassProgress < 0.65) bitPhase = 'in-field';
    else if (c.glassProgress >= 0.65 && c.glassProgress < 0.80) bitPhase = 'ascending';
    else if (c.glassProgress >= 0.80) bitPhase = 'logo (echo only)';

    hud.textContent = [
      `mode:                ${mode}`,
      `glassProgress:       ${c.glassProgress.toFixed(3)}`,
      `highestScrollProg:   ${c.highestScrollProgress.toFixed(3)}`,
      `fieldStoryTime:      ${c.fieldStoryTime.toFixed(3)}`,
      `highestStoryTime:    ${c.highestStoryTime.toFixed(3)}`,
      `cells > 0.15:        ${lit}/${total} (${litPct}%)`,
      `B2 (>=0.08, >=5%):   ${b2pass ? 'PASS' : 'pending'}`,
      `glass opacity:       ${(glassOp * 100).toFixed(0)}%`,
      `FieldBit phase:      ${bitPhase}`,
      `Viewer stage:        ${viewerStage.stage} (alpha=${viewerStage.alpha.toFixed(2)})`,
      `ghost residue:       ${residueApplied ? 'applied' : 'fresh visit'}`,
    ].join('\n');

    if (!isStatic) requestAnimationFrame(paint);
  }
  paint();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
